import { useFeatureGate } from '@statsig/expo-bindings';
import { LinearGradient } from 'expo-linear-gradient';
import * as React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    FullScreenSpinner,
    H5Regular,
    Icon,
    Image,
    ListChecksIcon,
    SafeAreaView,
    Shadows,
    SmoothieScrollView,
    SplitIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import imChart from '../../../assets/images/investing/invest-splash-chart.png';
import imData from '../../../assets/images/investing/invest-splash-data.png';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { useGetNextGuidanceSession } from '../../guidanceSessions/hooks/useGetNextGuidanceSession';
import { GuidanceSessionType } from '../../guidanceSessions/types/GuidanceSessions';
import ExpertAdviceCard from '../../home/components/guidance/ExpertAdviceCard';

interface RowComponentProps {
    icon: React.ReactElement;
    header: string;
    details: string;
}

const RowComponent = ({ icon, header, details }: RowComponentProps) => {
    return (
        <XStack gap={12} width="100%">
            <YStack
                width={32}
                height={32}
                alignItems="center"
                justifyContent="center"
            >
                {icon}
            </YStack>
            <YStack flex={1}>
                <BodyMediumRegular>{header}</BodyMediumRegular>
                <BodySmallRegular lineHeight={20} color="$gray900">
                    {details}
                </BodySmallRegular>
            </YStack>
        </XStack>
    );
};

export const InvestmentsSplash = () => {
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const {
        isLoading: isLoadingNextGuidanceSession,
        nextGuidanceOnboardingSessionType,
        nextGuidanceOnboardingSessionNeedsBooking,
    } = useGetNextGuidanceSession();

    const needsFirstOnboardingSessionBooking =
        nextGuidanceOnboardingSessionNeedsBooking &&
        nextGuidanceOnboardingSessionType === GuidanceSessionType.DISCOVERY;
    const showExpertAdviceCard =
        goyoEnabled && needsFirstOnboardingSessionBooking;

    useTrackViewOnFocus('Investment/Splash');

    if (goyoEnabled && isLoadingNextGuidanceSession) {
        return <FullScreenSpinner />;
    }

    return (
        <SafeAreaView>
            <SmoothieScrollView>
                <YStack
                    gap={16}
                    alignItems="center"
                    justifyContent="center"
                    padding={8}
                    paddingTop={24}
                    maxWidth={600}
                    $sm={{ padding: 48, gap: 32 }}
                >
                    <YStack gap={12} alignItems="center">
                        <H5Regular>Invest with Fruitful</H5Regular>
                        <BodyMediumRegular
                            textAlign="center"
                            lineHeight={24}
                            paddingHorizontal={8}
                        >
                            With a Fruitful Investment Account, your money is
                            expertly guided, tailored to your goals, and set to
                            grow.
                        </BodyMediumRegular>
                    </YStack>
                    <LinearGradient
                        colors={['#DAEDE4', '#B4D0D8']}
                        style={{
                            width: '100%',
                            borderRadius: 16,
                            paddingTop: 36,
                            paddingBottom: 48,
                        }}
                        pointerEvents="none"
                    >
                        <XStack justifyContent="center" alignItems="flex-start">
                            <View
                                transform={[
                                    { translateX: 90 },
                                    { translateY: 20 },
                                    { rotate: '9deg' },
                                ]}
                                {...Shadows.SMALL}
                            >
                                <Image
                                    source={imData}
                                    height={130}
                                    width={188}
                                    objectFit="contain"
                                    resizeMode="contain"
                                />
                            </View>
                            <View
                                transform={[
                                    { translateX: -150 },
                                    { rotate: '-4deg' },
                                ]}
                                {...Shadows.SMALL}
                            >
                                <Image
                                    source={imChart}
                                    height={175}
                                    width={129}
                                    objectFit="contain"
                                    resizeMode="contain"
                                />
                            </View>
                        </XStack>
                    </LinearGradient>
                    {showExpertAdviceCard && (
                        <ExpertAdviceCard
                            title="Open your investment accounts"
                            text="Book a session with one of our Guides to open investment accounts."
                        />
                    )}
                    <YStack
                        width="100%"
                        backgroundColor="$gray200"
                        borderRadius={16}
                        gap={16}
                        padding={12}
                        $sm={{ padding: 20, gap: 20 }}
                    >
                        <RowComponent
                            icon={
                                <View rotate="180deg">
                                    <Icon icon={SplitIcon} color="$gray1200" />
                                </View>
                            }
                            header="Integrated with your Money Map"
                            details="See your investments alongside your full financial plan."
                        />
                        <RowComponent
                            icon={
                                <Icon icon={ListChecksIcon} color="$gray1200" />
                            }
                            header="Stress-free setup"
                            details="We handle the details so you can start investing right away."
                        />
                    </YStack>
                    {!showExpertAdviceCard && (
                        <BodySmallRegular color="$gray1000" textAlign="center">
                            Talk to your Guide to open your investment account.
                        </BodySmallRegular>
                    )}
                </YStack>
            </SmoothieScrollView>
        </SafeAreaView>
    );
};
